import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { 
  MapPin, 
  User, 
  Calendar, 
  Clock, 
  Building2,
  Home,
  Phone,
  Mail,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Star,
  Navigation,
  MessageSquare,
  Camera
} from "lucide-react";
import { toast } from "sonner";
import { JobPhotoManager } from "./JobPhotoManager";

// Utility function to extract postcode from UK address
const extractPostcode = (address: string): string => {
  if (!address) return 'Location not specified';
  
  // Split by comma and trim each part
  const parts = address.split(',').map(part => part.trim());
  
  // UK postcode pattern: Letters/numbers followed by space and 3 characters
  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
  
  // Look for postcode in each part
  for (const part of parts) {
    if (postcodeRegex.test(part)) {
      return part;
    }
  }
  
  // Fallback: take last 2 parts after comma split (city, postcode area)
  if (parts.length >= 2) {
    return parts.slice(-2).join(', ');
  }
  
  return address;
};

// Utility function to get the appropriate location display based on job assignment
const getLocationDisplay = (job: Job, currentTradesmanId: string): string => {
  // If job is assigned to current tradesman, show full location
  if (job.tradesman_id === currentTradesmanId) {
    return job.location || 'Location not specified';
  }
  
  // Otherwise, show only postcode for privacy
  return extractPostcode(job.location || '');
};

interface CustomerProfile {
  full_name: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface Job {
  id: string;
  job_number: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  location: string;
  budget_min?: number;
  budget_max?: number;
  agreed_cost?: number;
  required_date?: string;
  required_specialties?: string[];
  job_category?: string;
  created_at: string;
  updated_at: string;
  response_by: string;
  customer_id: string;
  tradesman_id: string | null;
  distance?: number;
}

interface TradesmanJobDetailDialogProps {
  job: Job | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateToJob?: () => void;
  onMessageCustomer?: () => void;
}

export function TradesmanJobDetailDialog({
  job,
  open,
  onOpenChange,
  onNavigateToJob,
  onMessageCustomer,
}: TradesmanJobDetailDialogProps) {
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [hasCompletedPayment, setHasCompletedPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const [currentTradesmanId, setCurrentTradesmanId] = useState<string>('');

  useEffect(() => {
    if (job && open) {
      fetchJobDetails();
    }
  }, [job, open]);

  const fetchJobDetails = async () => {
    if (!job) return;
    
    setIsLoading(true);
    try {
      // Get current tradesman profile ID
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', userData.user.id)
          .single();
        
        if (profileData) {
          setCurrentTradesmanId(profileData.id);
        }
      }
      // Check if payment has been completed for this job using secure function
      const { data: paymentData, error: paymentError } = await supabase
        .rpc('get_job_payment_status', { job_id: job.id });

      if (paymentError) throw paymentError;
      
      const paymentCompleted = !!(paymentData && paymentData.length > 0 && paymentData[0]?.status === 'completed');
      setHasCompletedPayment(paymentCompleted);

      // Use the secure database function to fetch customer details
      console.log('Fetching customer details for job:', job.id, 'Status:', job.status);
      
      // For in-progress or completed jobs, fetch customer full contact details
      if (job.status === 'in_progress' || job.status === 'completed') {
        const { data: customerData, error: customerError } = await supabase
          .rpc('get_customer_details_for_job', { job_id: job.id });

        if (customerError) {
          console.error('Error fetching customer contact details:', customerError);
        }

        if (customerData && customerData.length > 0) {
          // Only fetch address for jobs that are assigned to this tradesman AND in progress
          let customerAddress = null;
          if (job.status === 'in_progress' && job.tradesman_id) {
            const { data: addressData, error: addressError } = await supabase
              .from('profiles')
              .select('address')
              .eq('id', job.customer_id)
              .single();

            if (addressError) {
              console.error('Error fetching customer address:', addressError);
            } else {
              customerAddress = addressData?.address || null;
            }
          }

          setCustomerProfile({
            full_name: customerData[0].full_name,
            email: customerData[0].email,
            phone: customerData[0].phone,
            address: customerAddress
          });
        } else {
          setCustomerProfile(null);
        }
      } else {
        // First try to get full contact details (for paid jobs)
        const { data: customerData, error: customerError } = await supabase
          .rpc('get_customer_details_for_job', { job_id: job.id });

        if (customerError) {
          console.error('Error fetching customer contact details:', customerError);
        }

        // If no contact details available, try to get public details
        if (!customerData || customerData.length === 0) {
          console.log('No contact details available, fetching public customer details');
          const { data: publicCustomerData, error: publicCustomerError } = await supabase
            .rpc('get_customer_public_details_for_tradesman', { job_id: job.id });

          if (publicCustomerError) {
            console.error('Error fetching public customer details:', publicCustomerError);
          } else if (publicCustomerData && publicCustomerData.length > 0) {
            console.log('Public customer details response:', publicCustomerData);
            setCustomerProfile({
              full_name: publicCustomerData[0].full_name,
              email: null, // No contact info for public details
              phone: null, // No contact info for public details
              address: (job.status === 'in_progress' || job.status === 'completed') ? publicCustomerData[0].address : null // Address only shown for in_progress/completed jobs
            });
          } else {
            console.log('No customer details available for this job');
            setCustomerProfile(null);
          }
        } else {
          console.log('Full customer details response:', customerData);
          setCustomerProfile({
            full_name: customerData[0].full_name,
            email: customerData[0].email,
            phone: customerData[0].phone,
            address: null // No address for jobs not in progress
          });
        }
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      toast.error("Failed to load job details");
    } finally {
      setIsLoading(false);
    }
  };

  if (!job) return null;

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "assigned": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-orange-100 text-orange-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "emergency": return "bg-red-100 text-red-800";
      case "urgent": return "bg-orange-100 text-orange-800";
      case "standard": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Job #{job.job_number} - {job.title}</span>
            <Badge className={getStatusColor(job.status)}>
              {job.status.replace('_', ' ')}
            </Badge>
            <Badge className={getPriorityColor(job.priority)}>
              {job.priority}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Job Information */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Description</Label>
              <p className="text-sm mt-1 p-3 bg-muted rounded-md">{job.description}</p>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{getLocationDisplay(job, currentTradesmanId)}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Created: {formatDate(job.created_at)}</span>
            </div>

            {job.required_date && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>Required Date: {formatDate(job.required_date)}</span>
              </div>
            )}

            {job.job_category && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Category</Label>
                <p className="text-sm mt-1">{job.job_category}</p>
              </div>
            )}

            {job.required_specialties && job.required_specialties.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Required Specialties</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {job.required_specialties.map((specialty, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {specialty}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Customer Details & Budget */}
          <div className="space-y-4">
            {/* Customer Information */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4" />
                <Label className="font-medium">Customer Details</Label>
              </div>
              
              {!hasCompletedPayment && job.status !== 'in_progress' && job.status !== 'completed' ? (
                <div className="text-center py-4">
                  <div className="text-muted-foreground text-sm mb-2">
                    Customer contact details will be visible once payment is completed or job is in progress
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Payment Required
                  </Badge>
                </div>
              ) : isLoading ? (
                <div className="text-center py-4">
                  <div className="text-muted-foreground text-sm">Loading customer details...</div>
                </div>
              ) : customerProfile ? (
                 <div className="space-y-3">
                   <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                     <div className="flex items-center gap-2 mb-2">
                       <Star className="h-4 w-4 text-green-600" />
                       <span className="text-sm font-medium text-green-800">
                         {hasCompletedPayment ? 'Payment Completed' : 'Job Active'}
                       </span>
                     </div>
                     <p className="text-sm text-green-700">Full customer details are now available</p>
                   </div>
                  
                   <div className="space-y-2 text-sm">
                     <p className="font-medium text-lg">{customerProfile.full_name}</p>
                     {customerProfile.email && (
                       <div className="flex items-center gap-2">
                         <Mail className="h-4 w-4 text-muted-foreground" />
                         <a href={`mailto:${customerProfile.email}`} className="text-blue-600 hover:underline">
                           {customerProfile.email}
                         </a>
                       </div>
                     )}
                     {customerProfile.phone && (
                       <div className="flex items-center gap-2">
                         <Phone className="h-4 w-4 text-muted-foreground" />
                         <a href={`tel:${customerProfile.phone}`} className="text-blue-600 hover:underline">
                           {customerProfile.phone}
                         </a>
                       </div>
                     )}
                     {customerProfile.address && (
                       <div className="flex items-center gap-2">
                         <Home className="h-4 w-4 text-muted-foreground" />
                         <span className="text-foreground">
                           {customerProfile.address}
                         </span>
                       </div>
                     )}
                   </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="text-muted-foreground text-sm">Customer details not available</div>
                </div>
              )}
            </div>

            {/* Budget Information */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4" />
                <Label className="font-medium">Budget & Payment</Label>
              </div>
              <div className="space-y-2 text-sm">
                {job.agreed_cost ? (
                  <p className="font-medium text-green-600 text-lg">
                    Your Quote: {formatCurrency(job.agreed_cost / 1.03)}
                  </p>
                ) : (
                  <>
                    {job.budget_min && (
                      <p>Minimum: {formatCurrency(job.budget_min)}</p>
                    )}
                    {job.budget_max && (
                      <p>Maximum: {formatCurrency(job.budget_max)}</p>
                    )}
                    {!job.budget_min && !job.budget_max && (
                      <p className="text-muted-foreground">Budget not specified</p>
                    )}
                  </>
                )}
                
                {hasCompletedPayment && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-800">
                    ✓ Payment completed
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />
        
        {/* View Photos Button - Always visible for tradesmen */}
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowPhotos(true)} 
            className="flex items-center gap-2"
          >
            <Camera className="h-4 w-4" />
            View Job Photos
          </Button>
          
            {/* Action Buttons */}
          {hasCompletedPayment && (
            <>
              {onNavigateToJob && (
                <Button onClick={onNavigateToJob} className="flex items-center gap-2">
                  <Navigation className="h-4 w-4" />
                  Navigate to Job
                </Button>
              )}
              {onMessageCustomer && (
                <Button variant="outline" onClick={onMessageCustomer} className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Message Customer
                </Button>
              )}
            </>
          )}

          {/* Notice about quote editing restriction */}
          {job.status && ['quote_accepted', 'assigned', 'in_progress', 'completed'].includes(job.status) && (
            <div className="w-full p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                📝 Quote modifications are not allowed after job assignment to ensure pricing transparency.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
      
      {/* Job Photos Dialog */}
      <JobPhotoManager 
        jobId={job.id}
        open={showPhotos}
        onOpenChange={setShowPhotos}
        isCustomer={false} // Tradesman viewing mode
      />
    </Dialog>
  );
}